// AppShell — the chrome wrapping every authenticated curator page.
//
// Top nav, current user, sign-out. Children render below in a centered
// max-width container. Auth-gating is handled in App.tsx via RequireAuth, so
// this component assumes a signed-in user.
//
// The nav is split by SUBJECT, not by permission. Editorial pages get tabs;
// the money pages — payouts, credits, tips, subscriptions, patrons — collapse
// into one dropdown, because eight of them across the bar crowded out the work
// the Curator actually exists for and started wrapping the row. They happen to
// all be admin-only, but that is incidental: they are grouped because they are
// a different job, and they want a different home eventually.
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";

interface Tab {
  to: string;
  label: string;
  /** Money/commerce pages. Admin-gated, and grouped under one dropdown. */
  money?: boolean;
}

const TABS: Tab[] = [
  { to: "/prompts", label: "AI Prompt Templates" },
  { to: "/dashboard", label: "Dashboard" },
  { to: "/desk", label: "Editor's Desk" },
  { to: "/broadcast", label: "Broadcast" },
  { to: "/studio", label: "Studio" },
  { to: "/graph", label: "Graph" },
  { to: "/context-cards", label: "Context Cards" },
  { to: "/curations", label: "Curations" },
  { to: "/admin/payouts", label: "Payouts", money: true },
  { to: "/admin/credits", label: "Credits", money: true },
  { to: "/admin/tips", label: "Tips", money: true },
  {
    to: "/admin/subscription-products",
    label: "Sub Products",
    money: true,
  },
  { to: "/admin/subscriptions", label: "Subs", money: true },
  { to: "/admin/subscription-payments", label: "Sub Pmts", money: true },
  { to: "/admin/patrons", label: "Patrons", money: true },
  { to: "/admin/patrons/program", label: "Patron Program", money: true },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navTabs = TABS.filter((t) => !t.money);
  const moneyTabs = user?.role === "admin" ? TABS.filter((t) => t.money) : [];

  // Longest prefix wins, so /admin/patrons/program selects itself rather than
  // /admin/patrons, and a detail route like /admin/payouts/:id keeps the group
  // looking active instead of falling back to the placeholder.
  const activeMoney = moneyTabs
    .filter((t) => pathname === t.to || pathname.startsWith(`${t.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div style={styles.outer}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.brandRow}>
            <span style={styles.brand}>Curator</span>
            <nav style={styles.nav}>
              {navTabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  style={({ isActive }) => ({
                    ...styles.tab,
                    ...(isActive ? styles.tabActive : null),
                  })}
                >
                  {tab.label}
                </NavLink>
              ))}
              {moneyTabs.length > 0 && (
                <select
                  aria-label="Money pages"
                  value={activeMoney?.to ?? ""}
                  onChange={(e) => {
                    if (e.target.value) navigate(e.target.value);
                  }}
                  style={{
                    ...styles.tab,
                    ...styles.moneySelect,
                    ...(activeMoney ? styles.tabActive : null),
                  }}
                >
                  <option value="">Money</option>
                  {moneyTabs.map((tab) => (
                    <option key={tab.to} value={tab.to}>
                      {tab.label}
                    </option>
                  ))}
                </select>
              )}
            </nav>
          </div>
          <div style={styles.userRow}>
            <span style={styles.userLabel}>
              @{user?.username}{" "}
              <span style={styles.userRole}>{user?.role ?? "user"}</span>
            </span>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main style={styles.main}>{children}</main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  outer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "var(--surface)",
    borderBottom: "1px solid var(--border)",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  headerInner: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 32,
  },
  brand: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.3,
    color: "var(--text-primary)",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  tab: {
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text-secondary)",
    borderRadius: "var(--radius-sm)",
    textDecoration: "none",
  },
  // Sized to match a tab rather than a form field: the browser draws its own
  // chevron, so the extra right padding keeps the label clear of it.
  moneySelect: {
    background: "transparent",
    border: "1px solid var(--border)",
    cursor: "pointer",
    paddingRight: 24,
    fontFamily: "inherit",
  },
  tabActive: {
    color: "var(--text-primary)",
    background: "rgba(255,255,255,0.06)",
  },
  userRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  userLabel: {
    fontSize: 13,
    color: "var(--text-secondary)",
  },
  userRole: {
    color: "var(--text-muted)",
    fontSize: 12,
    marginLeft: 4,
  },
  logoutBtn: {
    background: "transparent",
    color: "var(--text-secondary)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    padding: "5px 10px",
    fontSize: 12,
  },
  main: {
    flex: 1,
    maxWidth: 1280,
    width: "100%",
    margin: "0 auto",
    padding: "24px",
  },
};
